import { client } from "@handoverpro/shared/api-client/client";

import { firebaseAuth } from "~/lib/firebase";

const BASE_URL = import.meta.env.VITE_API_URL;
if (!BASE_URL) {
  throw new Error("VITE_API_URL is not set");
}

// Thrown on 423 Locked — e.g. a session another clinician has open.
export class ApiLockedError extends Error {
  readonly status = 423 as const;
  readonly response: Response;
  constructor(response: Response) {
    super(`Locked: ${response.url}`);
    this.name = "ApiLockedError";
    this.response = response;
  }
}

// Thrown on 410 Gone — e.g. an expired join code.
export class ApiExpiredError extends Error {
  readonly status = 410 as const;
  readonly response: Response;
  constructor(response: Response) {
    super(`Expired: ${response.url}`);
    this.name = "ApiExpiredError";
    this.response = response;
  }
}

client.setConfig({
  baseUrl: BASE_URL,
});

// Firebase SDK transparently refreshes ID tokens (~1h TTL) on getIdToken().
// We fetch a fresh one per request rather than caching: the SDK already
// caches in-memory and the network cost is negligible.
client.interceptors.request.use(async (request) => {
  const user = firebaseAuth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    request.headers.set("Authorization", `Bearer ${token}`);
  }
  return request;
});

client.interceptors.response.use((response) => {
  if (response.status === 401) {
    // Hard navigate so React state and any in-flight requests are torn down.
    window.location.assign("/login");
    return response;
  }
  if (response.status === 423) {
    throw new ApiLockedError(response);
  }
  if (response.status === 410) {
    throw new ApiExpiredError(response);
  }
  return response;
});

export { client };
export * from "@handoverpro/shared/api-client";
